import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeftRight,
  Building2,
  Cpu,
  Download,
  Layers,
  Plus,
  RotateCcw,
  Search,
  ShoppingBag,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { BatchOutPaymentRow, BatchOutPeriod, BatchOutReport } from "@tilt/pos-sdk";

import { MethodIconForType } from "../components/sale/MethodIconForType";
import { getPosClient } from "../lib/api";
import { formatCents } from "../lib/format";
import { resolveMethodColors, lookupMethodByTypeSubtype } from "../lib/payment-methods";
import { usePageTitle } from "../lib/use-page-title";
import { cn } from "../lib/utils";
import { Alert } from "../components/ui/Alert";
import { Card } from "../components/ui/Card";
import { TextInput } from "../components/ui/Field";
import { PageSpinner } from "../components/ui/Spinner";
import { LocationSubsetPicker } from "../components/scope/LocationSubsetPicker";

/**
 * Reports surface - V1-parity Batch Out reports above an End-of-Day
 * card kept from the prior implementation. The new top section is the
 * V1 reconciliation workflow operators are used to: pick a period
 * (Daily / Weekly / Monthly / Quarterly / Yearly), pick a date range,
 * see KPI totals across the period, then drill into Revenue-by-Type
 * and Revenue-by-Department breakdowns and a per-payment detail table
 * with CSV export. The detail table renders a Location column when
 * the active scope is wider than a single location, per the
 * 2026-05-03 scope-aware-tables rule.
 *
 * V1 used loud gradient cards everywhere; the V2 redesign keeps the
 * same information density on top of V2 design tokens (``bg-card`` /
 * ``shadow-elev-1`` / kicker labels / mono tabular-nums) so the page
 * reads as part of the same product as the rest of POS.
 */

const PERIODS: readonly BatchOutPeriod[] = ["daily", "weekly", "monthly", "quarterly", "yearly"];

export function ReportsPage() {
  const { t, i18n } = useTranslation();
  const [period, setPeriod] = useState<BatchOutPeriod>("daily");
  // V1 reports surface had ONE date field that anchored the period
  // - pick a date with "Daily" and it scopes to that day; pick a
  // date with "Monthly" and it scopes to that date's full month.
  // V2 mirrors that: ``anchor`` is the period start (the only thing
  // the operator picks); ``rangeForAnchor`` derives the matching
  // {from, to} for the current period. ``daily`` defaults to the
  // most recent *complete* business day so a Monday-morning render
  // reconciles last Friday, not "today" with $0.
  const initialAnchor = useMemo(() => formatUtcDate(defaultAnchorForPeriod("daily")), []);
  const [anchor, setAnchor] = useState<string>(initialAnchor);
  usePageTitle("reports");

  // Period click → land on the LAST COMPLETED period for that cadence.
  // Q2-in-progress with $0 is never the operator's intent on tab-click;
  // they want the most recently FINALISED window (last week, last month,
  // last quarter, last year). They can still pick any in-progress
  // anchor by hand via the date input. Daily falls back to
  // ``lastBusinessDayUtc`` (Mon-morning render reconciles last Friday).
  const onPeriodChange = (next: BatchOutPeriod): void => {
    setPeriod(next);
    setAnchor(formatUtcDate(defaultAnchorForPeriod(next)));
  };

  // The user pastes / picks any date in the input - snap to the
  // current period's boundary so the field always reads as the
  // period start. Picking 2026-05-15 with Monthly selected becomes
  // 2026-05-01 in the field.
  const onAnchorChange = (raw: string): void => {
    if (!raw) {
      setAnchor(raw);
      return;
    }
    const parsed = parseUtcDate(raw);
    if (Number.isNaN(parsed.getTime())) {
      setAnchor(raw);
      return;
    }
    const snapped = snapAnchorToPeriod(parsed, period);
    setAnchor(formatUtcDate(snapped));
  };

  // The active anchor expanded into the {from, to} window the API
  // accepts. Single source of truth - both the queryKey and the
  // params spread off of it.
  const range = useMemo(() => rangeForAnchor(anchor, period), [anchor, period]);

  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => getPosClient().getProfile(),
  });

  // Active membership drives both the (implicit) backend scope filter
  // and the frontend's decision on whether to render a Location column
  // - when the cashier is location-scoped they only ever see one
  // location's rows so the column would be redundant.
  const active = profile.data?.active_membership ?? null;
  const scopeKind = active?.scope_kind ?? "location";
  const showLocationColumn = scopeKind !== "location";

  // Org / partner / platform-scoped operators may want a consolidated
  // rollup OR a picked subset of their visible locations. Empty list
  // == consolidated (the default; what the V1 page always shipped).
  // Picker self-hides when the active scope is location-scoped (the
  // visible-locations response is a single row in that case so the
  // picker would be noise).
  const [locationIds, setLocationIds] = useState<ReadonlyArray<string>>([]);
  const showSubsetPicker = scopeKind !== "location";

  const report = useQuery({
    queryKey: [
      "pos",
      "reports",
      "batch-out-v1",
      {
        period,
        from: range.from,
        to: range.to,
        scopeKind,
        locationIds: [...locationIds].sort().join(","),
      },
    ],
    queryFn: () =>
      getPosClient().getBatchOutReport({
        period,
        date_from: range.from,
        date_to: range.to,
        ...(locationIds.length > 0 ? { location_ids: locationIds } : {}),
      }),
    enabled: !!range.from && !!range.to && range.from <= range.to && !!active,
    staleTime: 30_000,
  });

  if (profile.isPending) {
    return <PageSpinner label={t("common.loading")} />;
  }

  if (!active) {
    return (
      <div className="max-w-md py-12">
        <Alert tone="warn" title={t("reports.noLocationTitle")}>
          {t("reports.noLocationBody")}
        </Alert>
      </div>
    );
  }

  const data = report.data ?? null;
  const locale = i18n.language || "en";

  return (
    <div className="space-y-6 pb-12">
      {/* Page header - kicker / title / subtitle, same shape as
          TransactionsPage so the app reads coherently. The V1 page was
          titled "Batch Out Reports"; we keep that wording for the
          subtitle so operators recognise it. */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">
            {t("reports.kicker")}
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-strong sm:text-3xl xl:text-4xl">
            {t("reports.batchOut.pageTitle")}
          </h1>
          <p className="mt-1 text-sm text-muted">{t("reports.batchOut.pageSubtitle")}</p>
        </div>
        {showSubsetPicker && (
          <LocationSubsetPicker
            value={locationIds}
            onChange={setLocationIds}
            disabled={report.isFetching}
          />
        )}
      </header>

      {/* Compact filter strip - period tabs + single date input on
          one row. V1 parity: one date that anchors the period (the
          period start), not a from/to range. Picking 2026-04-15 with
          "Monthly" selected scopes the report to all of April. The
          derived window appears as a small caption below the input
          when the period is wider than a day. Earlier rev wrapped
          this in a tall ``rounded-xl`` card with the date pinned to
          the right edge - that left the middle of the card empty and
          read like a layout bug (2026-05-03 dogfood). The unified
          strip puts everything where the eye lands. */}
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-line pb-2">
        <PeriodTabs value={period} onChange={onPeriodChange} />
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted">
              {t(`reports.batchOut.anchorLabel.${period}`)}
            </span>
            <input
              type="date"
              value={anchor}
              onChange={(e) => onAnchorChange(e.target.value)}
              aria-label={t(`reports.batchOut.anchorLabel.${period}`)}
              className="min-h-tap rounded-md bg-surface-muted px-3 text-sm font-medium text-foreground ring-1 ring-inset ring-line focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
          </div>
          {period !== "daily" && (
            <p className="font-mono text-[0.6875rem] tabular-nums text-muted">
              {range.from} → {range.to}
            </p>
          )}
        </div>
      </div>

      {report.isError && (
        <Alert tone="error" title={t("reports.errorTitle")}>
          {report.error instanceof Error ? report.error.message : t("reports.errorBody")}
        </Alert>
      )}

      {report.isPending && range.from <= range.to && <PageSpinner label={t("common.loading")} />}

      {data && (
        <>
          <KpiStrip data={data} />
          <RevenueByType data={data} locationId={active.location?.id ?? null} />
          <RevenueByDepartment data={data} />
          <PaymentsDetailTable
            data={data}
            showLocationColumn={showLocationColumn}
            locale={locale}
            locationId={active.location?.id ?? null}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- //
// Period tabs - V1-style underlined tab row above the date range    //
// ---------------------------------------------------------------- //

function PeriodTabs(props: {
  readonly value: BatchOutPeriod;
  readonly onChange: (next: BatchOutPeriod) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="tablist"
      aria-label={t("reports.batchOut.periodLabel")}
      className="flex overflow-x-auto"
    >
      {PERIODS.map((p) => {
        const active = p === props.value;
        return (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => props.onChange(p)}
            className={cn(
              "min-h-tap whitespace-nowrap border-b-2 px-5 py-3 text-sm font-semibold transition-colors",
              active
                ? "border-brand-primary text-brand-primary"
                : "border-transparent text-muted hover:border-line hover:text-foreground",
              "focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary",
            )}
          >
            {t(`reports.batchOut.period.${p}`)}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- //
// KPI strip - headline tiles for the period totals. These are the   //
// "front page" of the report so they get a deliberately punchier    //
// treatment than the per-method Revenue cards below: a tone-tinted  //
// gradient backdrop, a soft corner glow, a larger pill icon, and a  //
// taller value. The Revenue cards stay quiet (white card + thin     //
// accent strip) so the visual hierarchy reads as headline → detail. //
// ---------------------------------------------------------------- //

type KpiTone = "emerald" | "blue" | "violet" | "amber";

const KPI_GRADIENT: Record<KpiTone, string> = {
  emerald:
    "from-emerald-100/80 via-emerald-50/60 to-card dark:from-emerald-900/40 dark:via-emerald-950/20 dark:to-card",
  blue: "from-blue-100/80 via-blue-50/60 to-card dark:from-blue-900/40 dark:via-blue-950/20 dark:to-card",
  violet:
    "from-violet-100/80 via-violet-50/60 to-card dark:from-violet-900/40 dark:via-violet-950/20 dark:to-card",
  amber:
    "from-amber-100/80 via-amber-50/60 to-card dark:from-amber-900/40 dark:via-amber-950/20 dark:to-card",
};

const KPI_GLOW: Record<KpiTone, string> = {
  emerald: "bg-emerald-400/30 dark:bg-emerald-500/20",
  blue: "bg-blue-400/30 dark:bg-blue-500/20",
  violet: "bg-violet-400/30 dark:bg-violet-500/20",
  amber: "bg-amber-400/30 dark:bg-amber-500/20",
};

const KPI_RING: Record<KpiTone, string> = {
  emerald: "ring-emerald-200/60 dark:ring-emerald-800/40",
  blue: "ring-blue-200/60 dark:ring-blue-800/40",
  violet: "ring-violet-200/60 dark:ring-violet-800/40",
  amber: "ring-amber-200/60 dark:ring-amber-800/40",
};

const KPI_ICON_RING: Record<KpiTone, string> = {
  emerald:
    "bg-emerald-100 text-emerald-700 ring-emerald-200/70 dark:bg-emerald-900/40 dark:text-emerald-300 dark:ring-emerald-800/50",
  blue: "bg-blue-100 text-blue-700 ring-blue-200/70 dark:bg-blue-900/40 dark:text-blue-300 dark:ring-blue-800/50",
  violet:
    "bg-violet-100 text-violet-700 ring-violet-200/70 dark:bg-violet-900/40 dark:text-violet-300 dark:ring-violet-800/50",
  amber:
    "bg-amber-100 text-amber-700 ring-amber-200/70 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-800/50",
};

const KPI_VALUE: Record<KpiTone, string> = {
  emerald: "text-emerald-800 dark:text-emerald-200",
  blue: "text-blue-800 dark:text-blue-200",
  violet: "text-violet-800 dark:text-violet-200",
  amber: "text-amber-800 dark:text-amber-200",
};

function KpiStrip({ data }: { readonly data: BatchOutReport }) {
  const { t } = useTranslation();
  const totals = data.totals;
  const currency = "USD"; // batches share a currency per scope; future:
  // surface from data once backend returns it on the totals.
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      <KpiCard
        tone="emerald"
        icon={<Wallet className="h-4 w-4" aria-hidden="true" />}
        label={t("reports.batchOut.totals.deposit")}
        value={formatCents(totals.deposit_cents, currency)}
      />
      <KpiCard
        tone="blue"
        icon={<ShoppingBag className="h-4 w-4" aria-hidden="true" />}
        label={t("reports.batchOut.totals.charges")}
        value={String(totals.charge_count)}
      />
      <KpiCard
        tone="violet"
        icon={<Plus className="h-4 w-4" aria-hidden="true" />}
        label={t("reports.batchOut.totals.surcharge")}
        value={formatCents(totals.surcharge_cents, currency)}
        muted={totals.surcharge_cents === 0}
      />
      <KpiCard
        tone="amber"
        icon={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
        label={t("reports.batchOut.totals.refunds")}
        value={formatCents(totals.refunded_cents, currency)}
        muted={totals.refunded_cents === 0}
      />
      <KpiCard
        tone="violet"
        icon={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
        label={t("reports.batchOut.totals.discounts")}
        value={formatCents(totals.discount_cents ?? 0, currency)}
        muted={(totals.discount_cents ?? 0) === 0}
      />
    </div>
  );
}

function KpiCard(props: {
  readonly tone: KpiTone;
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string;
  /** When true, render the card in a muted "no activity" state. The
   *  tone backdrop and icon stay (so the slot's identity is clear)
   *  but the value drops to ``text-muted`` so a zeroed metric reads
   *  as inactive instead of competing with the live numbers. */
  readonly muted?: boolean;
}) {
  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-2xl p-5 shadow-elev-2 ring-1 ring-inset",
        "bg-gradient-to-br",
        KPI_GRADIENT[props.tone],
        KPI_RING[props.tone],
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full blur-2xl",
          KPI_GLOW[props.tone],
        )}
      />
      <div className="relative flex items-start justify-between gap-3">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-muted">
          {props.label}
        </span>
        <span
          className={cn(
            "inline-flex h-9 w-9 items-center justify-center rounded-xl shadow-sm ring-1 ring-inset",
            KPI_ICON_RING[props.tone],
          )}
        >
          {props.icon}
        </span>
      </div>
      <p
        className={cn(
          "relative mt-3 font-mono text-3xl font-bold tabular-nums tracking-tight",
          props.muted ? "text-muted" : KPI_VALUE[props.tone],
        )}
      >
        {props.value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- //
// Revenue by Payment Type - 4-card grid (Cash / Check / Card / ACH) //
// ---------------------------------------------------------------- //

function RevenueByType({
  data,
  locationId,
}: {
  readonly data: BatchOutReport;
  readonly locationId: string | null;
}) {
  const { t } = useTranslation();
  const railsQuery = useQuery({
    enabled: locationId != null,
    queryKey: ["payment-rails", locationId],
    queryFn: () => getPosClient().getLocationPaymentRails(locationId!),
    staleTime: 5 * 60_000,
  });
  const rails = useMemo(() => railsQuery.data?.rails ?? [], [railsQuery.data?.rails]);

  const { dataMap, orderedTypes } = useMemo(() => {
    const map = new Map(data.by_payment_type.map((r) => [r.payment_type, r]));
    const seen = new Set<string>();
    const types = [
      ...rails
        .slice()
        .sort((a, b) => a.display_order - b.display_order)
        .map((r) => r.payment_type)
        .filter((pt) => {
          if (seen.has(pt)) return false;
          seen.add(pt);
          return true;
        }),
      ...data.by_payment_type.map((r) => r.payment_type).filter((pt) => !seen.has(pt)),
    ];
    return { dataMap: map, orderedTypes: types };
  }, [rails, data.by_payment_type]);

  const rows = orderedTypes.map((key) => {
    const r = dataMap.get(key);
    if (r) return r;
    return {
      payment_type: key,
      charge_count: 0,
      sale_cents: 0,
      refund_cents: 0,
      surcharge_cents: 0,
      surcharge_refund_cents: 0,
      deposit_cents: 0,
    };
  });

  return (
    <Card title={t("reports.batchOut.byType.title")}>
      <p className="mb-4 text-xs text-muted">{t("reports.batchOut.byType.body")}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map((r) => {
          const rail =
            lookupMethodByTypeSubtype(rails, r.payment_type, null) ??
            rails.find((rl) => rl.payment_type === r.payment_type) ??
            null;
          const colors = rail ? resolveMethodColors(rail) : null;
          const accent = colors?.bg ?? "bg-brand-primary";
          const accentStyle = colors?.bgStyle;
          return (
            <RevenueCard
              key={r.payment_type}
              label={t(`reports.batchOut.paymentType.${r.payment_type}`, {
                defaultValue: capitalize(r.payment_type),
              })}
              icon={
                <MethodIconForType
                  paymentType={r.payment_type}
                  paymentSubtype={null}
                  locationId={locationId}
                  className="h-4 w-4"
                />
              }
              accent={accent}
              accentStyle={accentStyle}
              row={r}
            />
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- //
// Revenue by Department - N-card grid                                //
// ---------------------------------------------------------------- //

function RevenueByDepartment({ data }: { readonly data: BatchOutReport }) {
  const { t } = useTranslation();
  const rows = data.by_department;
  if (rows.length === 0) {
    return (
      <Card title={t("reports.batchOut.byDept.title")}>
        <p className="text-sm text-muted">{t("reports.batchOut.byDept.empty")}</p>
      </Card>
    );
  }
  return (
    <Card title={t("reports.batchOut.byDept.title")}>
      <p className="mb-4 text-xs text-muted">{t("reports.batchOut.byDept.body")}</p>
      <div
        className={cn(
          "grid grid-cols-1 gap-3 sm:grid-cols-2",
          rows.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3",
        )}
      >
        {rows.map((r) => (
          <RevenueCard
            key={r.department_name}
            label={r.department_name}
            icon={<Building2 className="h-4 w-4" aria-hidden="true" />}
            accent="bg-brand-primary"
            row={r}
          />
        ))}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- //
// RevenueCard - shared shape for by-type / by-department breakdowns //
// ---------------------------------------------------------------- //

interface RevenueRow {
  readonly charge_count: number;
  readonly sale_cents: number;
  readonly refund_cents: number;
  readonly surcharge_cents: number;
  readonly surcharge_refund_cents: number;
  readonly deposit_cents: number;
}

function RevenueCard(props: {
  readonly label: string;
  readonly icon: React.ReactNode;
  /** Tailwind ``bg-*`` class for the colored top-accent strip. */
  readonly accent: string;
  /** Inline style override for custom hex colors; mutually exclusive with `accent`. */
  readonly accentStyle?: CSSProperties | undefined;
  readonly row: RevenueRow;
}) {
  const { t } = useTranslation();
  const currency = "USD";
  const isEmpty = props.row.charge_count === 0;
  return (
    <div className="relative overflow-hidden rounded-xl bg-card p-4 shadow-elev-1">
      <span
        aria-hidden="true"
        className={cn("absolute inset-x-0 top-0 h-1", props.accent)}
        style={props.accentStyle}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-strong">
          {props.icon}
          <span className="text-sm font-semibold">{props.label}</span>
        </div>
        <span
          className={cn(
            "rounded-full px-2 py-0.5 font-mono text-[0.6875rem] tabular-nums",
            isEmpty ? "text-faint" : "bg-surface-muted text-foreground",
          )}
          title={t("reports.batchOut.lines.chargesCount", {
            defaultValue: "{{count}} charges",
            count: props.row.charge_count,
          })}
        >
          {props.row.charge_count}
        </span>
      </div>
      <dl className="mt-3 space-y-1 text-xs">
        <RevenueLine
          label={t("reports.batchOut.lines.sale")}
          value={formatCents(props.row.sale_cents, currency)}
        />
        <RevenueLine
          label={t("reports.batchOut.lines.surcharge")}
          value={formatCents(props.row.surcharge_cents, currency)}
        />
        <RevenueLine
          label={t("reports.batchOut.lines.refund")}
          value={formatCents(props.row.refund_cents, currency)}
          {...(props.row.refund_cents > 0 ? { tone: "warn" as const } : {})}
        />
        <RevenueLine
          label={t("reports.batchOut.lines.surchargeRefund")}
          value={formatCents(props.row.surcharge_refund_cents, currency)}
          {...(props.row.surcharge_refund_cents > 0 ? { tone: "warn" as const } : {})}
        />
      </dl>
      <div className="mt-3 flex items-baseline justify-between border-t border-line pt-3">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
          {t("reports.batchOut.lines.deposit")}
        </span>
        <span
          className={cn(
            "font-mono text-lg font-bold tabular-nums",
            isEmpty ? "text-muted" : "text-strong",
          )}
        >
          {formatCents(props.row.deposit_cents, currency)}
        </span>
      </div>
    </div>
  );
}

function RevenueLine(props: {
  readonly label: string;
  readonly value: string;
  readonly tone?: "warn";
}) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-muted">{props.label}</dt>
      <dd
        className={cn(
          "font-mono tabular-nums text-foreground",
          props.tone === "warn" && "text-amber-700 dark:text-amber-400",
        )}
      >
        {props.value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------- //
// Payments detail table - V1's "Payments by Type" / "by Device"     //
// merged into a single table with a filter pill row + search +     //
// CSV export. Filter pills toggle a Type | Device | All view.      //
// Charges and refunds both render; refund rows are tinted amber.   //
// Capped to 1000 rows server-side; we render all of them.           //
// ---------------------------------------------------------------- //

type DetailFilter = "all" | "type" | "device";

function PaymentsDetailTable(props: {
  readonly data: BatchOutReport;
  readonly showLocationColumn: boolean;
  readonly locale: string;
  readonly locationId: string | null;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<DetailFilter>("all");
  const [search, setSearch] = useState("");
  const allRows = props.data.payments;

  const filtered = useMemo(() => {
    let rows: ReadonlyArray<BatchOutPaymentRow> = allRows;
    if (filter === "type") {
      // "By Type" matches V1's view where rows lacking a device id (Cash
      // / Check / pay-by-text) are listed and grouped by payment_type.
      rows = rows.filter((r) => !r.device_id);
    } else if (filter === "device") {
      // "By Device" matches V1's card-present view - only rows that
      // carry a device_id (Valor card-present plugin stamps it).
      rows = rows.filter((r) => !!r.device_id);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => rowMatchesSearch(r, q));
    }
    return rows;
  }, [allRows, filter, search]);

  return (
    <Card>
      <div className="flex flex-col gap-3 border-b border-line pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
            {t("reports.batchOut.detail.kicker")}
          </p>
          <h3 className="mt-1 text-base font-semibold text-strong">
            {t("reports.batchOut.detail.title")}
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FilterPill
            active={filter === "all"}
            label={t("reports.batchOut.detail.filterAll")}
            icon={<Layers className="h-3.5 w-3.5" />}
            onClick={() => setFilter("all")}
          />
          <FilterPill
            active={filter === "type"}
            label={t("reports.batchOut.detail.filterType")}
            icon={<ShoppingBag className="h-3.5 w-3.5" />}
            onClick={() => setFilter("type")}
          />
          <FilterPill
            active={filter === "device"}
            label={t("reports.batchOut.detail.filterDevice")}
            icon={<Cpu className="h-3.5 w-3.5" />}
            onClick={() => setFilter("device")}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 sm:max-w-sm">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint"
            aria-hidden="true"
          />
          <TextInput
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("reports.batchOut.detail.searchPlaceholder")}
            className="min-h-tap pl-9"
          />
        </div>
        <button
          type="button"
          onClick={() => downloadDetailCsv(filtered, props.showLocationColumn)}
          className="inline-flex min-h-tap items-center gap-2 rounded-md bg-surface px-4 text-sm font-medium text-foreground shadow-elev-1 transition hover:bg-surface-muted"
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          {t("reports.batchOut.detail.exportCsv")}
        </button>
      </div>

      {filtered.length === 0 ? (
        <p className="mt-6 text-sm text-muted">{t("reports.batchOut.detail.empty")}</p>
      ) : (
        <div className="-mx-5 -mb-5 mt-4 overflow-x-auto sm:-mx-6 sm:-mb-6">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-left text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
              <tr>
                <th scope="col" className="px-5 py-3 sm:px-6">
                  {t("reports.batchOut.detail.colDate")}
                </th>
                <th scope="col" className="px-3 py-3">
                  {t("reports.batchOut.detail.colReference")}
                </th>
                {props.showLocationColumn && (
                  <th scope="col" className="px-3 py-3">
                    {t("reports.batchOut.detail.colLocation")}
                  </th>
                )}
                <th scope="col" className="px-3 py-3">
                  {t("reports.batchOut.detail.colType")}
                </th>
                <th scope="col" className="px-3 py-3">
                  {t("reports.batchOut.detail.colDepartment")}
                </th>
                <th scope="col" className="px-3 py-3">
                  {t("reports.batchOut.detail.colDevice")}
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  {t("reports.batchOut.detail.colAmount")}
                </th>
                <th scope="col" className="px-3 py-3">
                  {t("reports.batchOut.detail.colCustomer")}
                </th>
                <th scope="col" className="px-5 py-3 sm:px-6">
                  {t("reports.batchOut.detail.colRole")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {filtered.map((r, i) => (
                <DetailRow
                  key={r.payment_id}
                  row={r}
                  zebra={i % 2 === 1}
                  showLocationColumn={props.showLocationColumn}
                  locationId={props.locationId}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function FilterPill(props: {
  readonly active: boolean;
  readonly label: string;
  readonly icon: React.ReactNode;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-pressed={props.active}
      className={cn(
        "inline-flex min-h-tap items-center gap-1.5 rounded-full px-3 text-xs font-semibold transition",
        props.active
          ? "bg-card text-strong shadow-elev-1 ring-1 ring-inset ring-brand-primary/30"
          : "bg-surface-muted text-muted hover:text-foreground",
      )}
    >
      {props.icon}
      {props.label}
    </button>
  );
}

function DetailRow(props: {
  readonly row: BatchOutPaymentRow;
  readonly zebra: boolean;
  readonly showLocationColumn: boolean;
  readonly locationId: string | null;
}) {
  const r = props.row;
  const isRefund = r.link_role === "refund";
  const amountCents = isRefund ? r.applied_amount_cents : r.approved_cents;
  return (
    <tr className={cn(props.zebra && "bg-surface-muted/40")}>
      <td className="whitespace-nowrap px-5 py-3 font-mono text-xs tabular-nums text-foreground sm:px-6">
        {formatTimestamp(r.created_at)}
      </td>
      <td className="px-3 py-3 font-mono text-xs text-strong">
        {r.order_reference ?? r.external_ref ?? "-"}
      </td>
      {props.showLocationColumn && (
        <td className="px-3 py-3 text-foreground">
          {r.location_name ?? <span className="text-faint">-</span>}
        </td>
      )}
      <td className="px-3 py-3 capitalize text-foreground">
        <span className="inline-flex items-center gap-1.5">
          <MethodIconForType
            paymentType={r.payment_type ?? ""}
            paymentSubtype={r.payment_subtype ?? null}
            locationId={props.locationId}
            className="h-4 w-4"
          />
          {r.payment_type ?? "-"}
          {r.payment_subtype ? (
            <span className="text-xs text-muted">· {r.payment_subtype}</span>
          ) : null}
        </span>
      </td>
      <td className="px-3 py-3 text-foreground">{r.department_name}</td>
      <td className="px-3 py-3 font-mono text-xs text-muted">
        {r.device_id ?? <span className="text-faint">-</span>}
      </td>
      <td
        className={cn(
          "whitespace-nowrap px-3 py-3 text-right font-mono font-semibold tabular-nums",
          isRefund ? "text-amber-700 dark:text-amber-400" : "text-strong",
        )}
      >
        {isRefund ? "−" : ""}
        {formatCents(amountCents, "USD")}
      </td>
      <td className="px-3 py-3 text-foreground">
        {r.customer_name ?? <span className="text-faint">-</span>}
      </td>
      <td className="whitespace-nowrap px-5 py-3 sm:px-6">
        <RoleBadge role={r.link_role} />
      </td>
    </tr>
  );
}

function RoleBadge({ role }: { readonly role: BatchOutPaymentRow["link_role"] }) {
  const { t } = useTranslation();
  if (role === "refund") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[0.6875rem] font-semibold text-amber-800 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-800/40">
        <ArrowLeftRight className="h-3 w-3" aria-hidden="true" />
        {t("reports.batchOut.detail.refund")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[0.6875rem] font-semibold text-emerald-800 ring-1 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-800/40">
      <TrendingUp className="h-3 w-3" aria-hidden="true" />
      {t("reports.batchOut.detail.charge")}
    </span>
  );
}

// ---------------------------------------------------------------- //
// Helpers                                                           //
// ---------------------------------------------------------------- //

function rowMatchesSearch(r: BatchOutPaymentRow, q: string): boolean {
  const haystack = [
    r.order_reference,
    r.external_ref,
    r.payment_id,
    r.customer_name,
    r.customer_phone,
    r.payment_type,
    r.payment_subtype,
    r.department_name,
    r.device_id,
    r.location_name,
  ]
    .filter((s): s is string => !!s)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

function capitalize(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function formatTimestamp(iso: string): string {
  // Backend ships an ISO-ish string with a space separator. Show the
  // first 19 chars (YYYY-MM-DD HH:MM:SS) - finer precision is noise on
  // a reconciliation report.
  if (!iso) return "-";
  return iso.slice(0, 19).replace("T", " ");
}

function formatUtcDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse a ``YYYY-MM-DD`` string into a midnight-UTC ``Date``. The
 *  built-in ``new Date("YYYY-MM-DD")`` parses as UTC midnight too,
 *  but it's brittle on Safari for inputs without an explicit ``Z``;
 *  going through Date.UTC() is the deterministic shape. */
function parseUtcDate(iso: string): Date {
  const parts = iso.split("-").map((s) => parseInt(s, 10));
  if (parts.length !== 3 || parts.some(Number.isNaN)) {
    return new Date(NaN);
  }
  const [y, m, d] = parts as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}

/** Most recent *complete* business day. Operators run reconciliation
 *  at the start of the next shift, so the natural Day view is
 *  yesterday (if yesterday was Mon-Fri) or last Friday (if yesterday
 *  was Sat / Sun). Walks back from yesterday until it lands on a
 *  weekday. Note: holidays are NOT skipped - the V1 reference treats
 *  Memorial Day / July 4 / etc as zero-volume business days rather
 *  than skipping them, and matching that keeps the report tally
 *  consistent across years. */
function lastBusinessDayUtc(): Date {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - 1);
  while (d.getUTCDay() === 0 || d.getUTCDay() === 6) {
    d.setUTCDate(d.getUTCDate() - 1);
  }
  return d;
}

/** Default anchor for a tab-click. Returns the LAST COMPLETED period
 *  (operators want a finalised window on landing, not Q2-in-progress
 *  with $0). Hand-picks via the date input still snap to the
 *  containing period via ``snapAnchorToPeriod``.
 *
 *    - daily:     last business day (Mon → last Fri)
 *    - weekly:    Monday of LAST week
 *    - monthly:   1st of LAST month
 *    - quarterly: 1st of LAST quarter (today in Q2 → 2026-01-01)
 *    - yearly:    Jan 1 of LAST year */
function defaultAnchorForPeriod(period: BatchOutPeriod): Date {
  if (period === "daily") return lastBusinessDayUtc();
  const today = new Date();
  const t = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  switch (period) {
    case "weekly": {
      const dow = t.getUTCDay();
      const offset = dow === 0 ? 6 : dow - 1;
      t.setUTCDate(t.getUTCDate() - offset - 7);
      return t;
    }
    case "monthly":
      return new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() - 1, 1));
    case "quarterly": {
      const q = Math.floor(t.getUTCMonth() / 3);
      const lastQ = q - 1;
      const year = lastQ < 0 ? t.getUTCFullYear() - 1 : t.getUTCFullYear();
      const month = lastQ < 0 ? 9 : lastQ * 3;
      return new Date(Date.UTC(year, month, 1));
    }
    case "yearly":
      return new Date(Date.UTC(t.getUTCFullYear() - 1, 0, 1));
    default:
      return t;
  }
}

/** Snap ``d`` to the start of the period containing it. This is the
 *  V1 "pick any date, scope to its period" behaviour: pick 2026-05-15
 *  with Monthly selected → snap to 2026-05-01; pick 2026-08-09 with
 *  Quarterly → snap to 2026-07-01.
 *
 *    - daily:     no snap (the day itself)
 *    - weekly:    Monday of the week containing ``d`` (ISO Mon-start)
 *    - monthly:   1st of the month containing ``d``
 *    - quarterly: 1st of the quarter containing ``d`` (Jan/Apr/Jul/Oct)
 *    - yearly:    Jan 1 of the year containing ``d`` */
function snapAnchorToPeriod(d: Date, period: BatchOutPeriod): Date {
  if (Number.isNaN(d.getTime())) return d;
  const out = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  switch (period) {
    case "daily":
      return out;
    case "weekly": {
      // JS getUTCDay: Sun=0, Mon=1, ..., Sat=6. ISO week is Mon-start.
      const dow = out.getUTCDay();
      const offset = dow === 0 ? 6 : dow - 1;
      out.setUTCDate(out.getUTCDate() - offset);
      return out;
    }
    case "monthly":
      return new Date(Date.UTC(out.getUTCFullYear(), out.getUTCMonth(), 1));
    case "quarterly": {
      const q = Math.floor(out.getUTCMonth() / 3);
      return new Date(Date.UTC(out.getUTCFullYear(), q * 3, 1));
    }
    case "yearly":
      return new Date(Date.UTC(out.getUTCFullYear(), 0, 1));
    default:
      return out;
  }
}

/** End of the period anchored at ``start``. Inclusive - the last day
 *  the period covers. ``monthly`` uses the day-zero-of-next-month
 *  trick to land on the last calendar day regardless of length. */
function endOfPeriodUtc(start: Date, period: BatchOutPeriod): Date {
  if (Number.isNaN(start.getTime())) return start;
  const y = start.getUTCFullYear();
  const m = start.getUTCMonth();
  const d = start.getUTCDate();
  switch (period) {
    case "daily":
      return new Date(Date.UTC(y, m, d));
    case "weekly":
      return new Date(Date.UTC(y, m, d + 6));
    case "monthly":
      // Day 0 of next month = last day of this month.
      return new Date(Date.UTC(y, m + 1, 0));
    case "quarterly":
      // Day 0 of (start.month + 3) = last day of the quarter.
      return new Date(Date.UTC(y, m + 3, 0));
    case "yearly":
      return new Date(Date.UTC(y, 11, 31));
    default:
      return new Date(Date.UTC(y, m, d));
  }
}

/** Expand a single anchor date + period into the {from, to} window
 *  the API takes. The anchor is treated as the period start (caller
 *  is responsible for snapping); the end is computed and capped at
 *  today so a future-anchored period doesn't ask the API for rows
 *  that can't exist yet. */
function rangeForAnchor(
  anchorIso: string,
  period: BatchOutPeriod,
): { readonly from: string; readonly to: string } {
  const start = parseUtcDate(anchorIso);
  if (Number.isNaN(start.getTime())) {
    return { from: anchorIso, to: anchorIso };
  }
  const end = endOfPeriodUtc(start, period);
  const today = new Date();
  const todayUtc = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
  );
  const cappedEnd = end.getTime() > todayUtc.getTime() ? todayUtc : end;
  return {
    from: formatUtcDate(start),
    to: formatUtcDate(cappedEnd),
  };
}

// ---------------------------------------------------------------- //
// CSV export                                                        //
// ---------------------------------------------------------------- //

function csvEscape(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadDetailCsv(
  rows: ReadonlyArray<BatchOutPaymentRow>,
  showLocationColumn: boolean,
): void {
  const headers: string[] = [
    "payment_id",
    "created_at",
    "external_ref",
    "order_reference",
    "payment_type",
    "payment_subtype",
    "provider_name",
    "department_name",
    "device_id",
    "customer_name",
    "customer_phone",
    "role",
    "approved_cents",
    "applied_amount_cents",
    "surcharge_cents",
  ];
  if (showLocationColumn) {
    // Insert the Location column right after the timestamp so the
    // CSV reads like the on-screen table - operators sort by location
    // first when reconciling cross-location aggregates.
    headers.splice(2, 0, "location_id", "location_name");
  }
  const rowsCsv = rows.map((r) => {
    const cells: (string | number | null | undefined)[] = [
      r.payment_id,
      r.created_at,
      r.external_ref,
      r.order_reference,
      r.payment_type,
      r.payment_subtype,
      r.provider_name,
      r.department_name,
      r.device_id,
      r.customer_name,
      r.customer_phone,
      r.link_role,
      r.approved_cents,
      r.applied_amount_cents,
      r.surcharge_cents,
    ];
    if (showLocationColumn) {
      cells.splice(2, 0, r.location_id, r.location_name);
    }
    return cells.map(csvEscape).join(",");
  });
  const csv = [headers.join(","), ...rowsCsv].join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `batch-out-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
